// Zustand store: session lifecycle + chat thread.
//
// Lives at app scope (not Chat-local) so navigation Chat → Recommendation →
// Account doesn't lose the session_id, the accumulated thread, or the
// resolved profile. Survives in-app navigation; lost on full page refresh
// (POC scope — v0.4 will persist to localStorage / cookie).

import { create } from "zustand";

import type { ActiveSessionResponse, ChatResponse } from "@incorposano/api-client";

// Internal message shape. Keeps the API's reply payload at hand for audit
// (intake_complete + profile snapshots travel with the message that triggered
// them) while exposing a thin role/text view to the chat UI.
export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Present only on assistant messages that closed the intake. */
  completionPayload?: ChatResponse;
}

interface SessionState {
  sessionId: string | null;
  greeting: string | null;
  thread: ChatMessage[];
  profile: ChatResponse["profile"];
  intakeComplete: boolean;

  // ─── actions ───────────────────────────────────────────
  startSession(args: { sessionId: string; greeting: string }): void;
  hydrateSession(active: ActiveSessionResponse): void;
  appendUser(text: string): void;
  appendAssistant(reply: ChatResponse): void;
  /** Drop the trailing assistant turns + the last user turn from the local
   *  thread. Mirror of backend DELETE /sessions/{id}/messages/last. */
  popLastPair(): void;
  /** Switch the active sessionId without fetching anything. The screens
   *  read it from the store and refetch keyed off the new id. */
  setSessionId(sessionId: string): void;
  reset(): void;
}

let messageSeq = 0;
const nextId = () => `m_${Date.now().toString(36)}_${(messageSeq++).toString(36)}`;

export const useSession = create<SessionState>((set) => ({
  sessionId: null,
  greeting: null,
  thread: [],
  profile: null,
  intakeComplete: false,

  startSession: ({ sessionId, greeting }) =>
    set({
      sessionId,
      greeting,
      thread: [{ id: nextId(), role: "assistant", text: greeting }],
      profile: null,
      intakeComplete: false,
    }),

  hydrateSession: (active) =>
    set({
      sessionId: active.session_id,
      greeting: active.greeting_message,
      thread: active.thread.map((t) => ({
        id: nextId(),
        role: t.role === "user" ? "user" : "assistant",
        text: t.text,
      })),
      profile: active.profile ?? null,
      intakeComplete: active.intake_complete,
    }),

  appendUser: (text) =>
    set((s) => ({
      thread: [...s.thread, { id: nextId(), role: "user", text }],
    })),

  appendAssistant: (reply) =>
    set((s) => {
      const message: ChatMessage = {
        id: nextId(),
        role: "assistant",
        text: reply.message,
        ...(reply.intake_complete ? { completionPayload: reply } : {}),
      };
      return {
        thread: [...s.thread, message],
        profile: reply.profile ?? s.profile,
        intakeComplete: reply.intake_complete || s.intakeComplete,
      };
    }),

  popLastPair: () =>
    set((s) => {
      // Walk back from the tail collecting trailing assistant messages,
      // then the single user message that triggered them. Trim them all.
      let cut = s.thread.length;
      while (cut > 0 && s.thread[cut - 1]?.role === "assistant") cut -= 1;
      if (cut > 0 && s.thread[cut - 1]?.role === "user") cut -= 1;
      return { thread: s.thread.slice(0, cut) };
    }),

  setSessionId: (sessionId: string) =>
    set({
      sessionId,
      // Clearing thread/profile on a switch keeps Recommendation/Chat from
      // rendering stale content from the previous session while their
      // queries refetch keyed off the new id.
      thread: [],
      profile: null,
      intakeComplete: false,
      greeting: null,
    }),

  reset: () =>
    set({
      sessionId: null,
      greeting: null,
      thread: [],
      profile: null,
      intakeComplete: false,
    }),
}));
